import React, { useState, useEffect, useCallback } from 'react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { 
  Plus, Search, Trash2, Edit2, Check, X, Loader2, 
  Percent, Euro, Gift, Calendar, Users, RefreshCw,
  ToggleLeft, ToggleRight, Infinity
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { marketingApi } from '../lib/api';

// Status badge
const StatusBadge = ({ code }) => {
  const maxUses = code.max_uses || 1;
  const currentUses = code.current_uses || 0;
  const isExpired = new Date(code.expires_at) < new Date();
  const isExhausted = maxUses > 0 && currentUses >= maxUses;
  const isInactive = !code.is_active;

  if (isInactive) {
    return <Badge className="bg-zinc-500/20 text-zinc-400">Desactivado</Badge>;
  }
  if (isExpired) {
    return <Badge className="bg-red-500/20 text-red-400">Expirado</Badge>;
  }
  if (isExhausted) {
    return <Badge className="bg-amber-500/20 text-amber-400">Agotado</Badge>;
  }
  return <Badge className="bg-green-500/20 text-green-400">Activo</Badge>;
};

// Type badge
const TypeBadge = ({ type, value }) => {
  if (type === 'PERCENTAGE') {
    return (
      <div className="flex items-center gap-1 text-purple-400">
        <Percent className="w-4 h-4" />
        <span className="font-bold">{value}%</span>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-1 text-green-400">
      <Euro className="w-4 h-4" />
      <span className="font-bold">{(value / 100).toFixed(2)}€</span>
    </div>
  );
};

const DiscountsManager = () => {
  const [discounts, setDiscounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [filterActive, setFilterActive] = useState('');
  
  // Create dialog
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newDiscount, setNewDiscount] = useState({
    name: '',
    code: '',
    type: 'PERCENTAGE',
    value: 10,
    max_uses: 1,
    min_amount_cents: 0,
    valid_days: 30,
    contact_email: ''
  });
  
  // Edit dialog
  const [showEdit, setShowEdit] = useState(false);
  const [editingCode, setEditingCode] = useState(null);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);

  // Fetch discounts
  const fetchDiscounts = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page,
        limit: 20,
        search: search || undefined,
        active: filterActive === 'active' ? true : filterActive === 'inactive' ? false : undefined
      };
      const response = await marketingApi.getDiscountCodes(params);
      setDiscounts(response.data.codes);
      setTotal(response.data.total);
    } catch (error) {
      console.error('Error fetching discounts:', error);
      toast.error('Error al cargar descuentos');
    } finally {
      setLoading(false);
    }
  }, [page, search, filterActive]);

  useEffect(() => {
    fetchDiscounts();
  }, [fetchDiscounts]);

  // Create discount
  const handleCreate = async () => {
    if (!newDiscount.name) {
      toast.error('Introduce un nombre para el descuento');
      return;
    }
    if (newDiscount.value <= 0) {
      toast.error('El valor debe ser mayor que 0');
      return;
    }

    setCreating(true);
    try {
      const data = {
        name: newDiscount.name,
        code: newDiscount.code || undefined,
        type: newDiscount.type,
        value: newDiscount.type === 'FIXED' 
          ? Math.round(newDiscount.value * 100) // Convert euros to cents
          : newDiscount.value,
        max_uses: newDiscount.max_uses,
        min_amount_cents: Math.round(newDiscount.min_amount_cents * 100),
        valid_days: newDiscount.valid_days,
        contact_email: newDiscount.contact_email || undefined
      };

      const response = await marketingApi.createDiscountCode(data);
      toast.success(`Código ${response.data.code} creado`);
      setShowCreate(false);
      setNewDiscount({
        name: '',
        code: '',
        type: 'PERCENTAGE',
        value: 10,
        max_uses: 1,
        min_amount_cents: 0,
        valid_days: 30,
        contact_email: ''
      });
      fetchDiscounts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear descuento');
    } finally {
      setCreating(false);
    }
  };

  // Edit discount
  const openEdit = (discount) => {
    setEditingCode(discount.code);
    setEditData({
      name: discount.name || '',
      is_active: discount.is_active !== false,
      max_uses: discount.max_uses || 1,
      min_amount_cents: (discount.min_amount_cents || 0) / 100,
      value: discount.type === 'FIXED' ? (discount.value / 100) : discount.value
    });
    setShowEdit(true);
  };

  const handleUpdate = async () => {
    setSaving(true);
    try {
      const data = {
        name: editData.name,
        is_active: editData.is_active,
        max_uses: editData.max_uses,
        min_amount_cents: Math.round(editData.min_amount_cents * 100),
        value: discounts.find(d => d.code === editingCode)?.type === 'FIXED'
          ? Math.round(editData.value * 100)
          : editData.value
      };

      await marketingApi.updateDiscountCode(editingCode, data);
      toast.success('Descuento actualizado');
      setShowEdit(false);
      fetchDiscounts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar');
    } finally {
      setSaving(false);
    }
  };

  // Delete discount
  const handleDelete = async (code) => {
    if (!confirm(`¿Eliminar el código ${code}? Esta acción no se puede deshacer.`)) return;

    try {
      await marketingApi.deleteDiscountCode(code);
      toast.success('Código eliminado');
      fetchDiscounts();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  // Toggle active
  const toggleActive = async (code, currentActive) => {
    try {
      await marketingApi.updateDiscountCode(code, { is_active: !currentActive });
      toast.success(currentActive ? 'Código desactivado' : 'Código activado');
      fetchDiscounts();
    } catch (error) {
      toast.error('Error al cambiar estado');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Gestión de Descuentos</h2>
          <p className="text-zinc-500">Crea y administra códigos de descuento</p>
        </div>
        <Button onClick={() => setShowCreate(true)} className="bg-primary text-black">
          <Plus className="w-4 h-4 mr-2" />
          Nuevo Descuento
        </Button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="flex-1 max-w-sm">
          <Input
            placeholder="Buscar por código, nombre..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="bg-secondary border-white/10"
          />
        </div>
        <Select value={filterActive} onValueChange={setFilterActive}>
          <SelectTrigger className="w-[150px] bg-secondary border-white/10">
            <SelectValue placeholder="Estado" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="active">Activos</SelectItem>
            <SelectItem value="inactive">Inactivos</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" onClick={fetchDiscounts} className="border-white/10">
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      {/* Table */}
      <div className="card-dark overflow-hidden">
        {loading ? (
          <div className="p-4 space-y-3">
            {[...Array(5)].map((_, i) => (
              <Skeleton key={`discount-skeleton-${i}`} className="h-16 w-full bg-white/5" />
            ))}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/5">
                  <th className="text-left p-4 text-xs text-zinc-500 font-medium">Código</th>
                  <th className="text-left p-4 text-xs text-zinc-500 font-medium">Nombre</th>
                  <th className="text-left p-4 text-xs text-zinc-500 font-medium">Valor</th>
                  <th className="text-left p-4 text-xs text-zinc-500 font-medium">Usos</th>
                  <th className="text-left p-4 text-xs text-zinc-500 font-medium">Expira</th>
                  <th className="text-left p-4 text-xs text-zinc-500 font-medium">Estado</th>
                  <th className="text-left p-4 text-xs text-zinc-500 font-medium">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {discounts.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="p-8 text-center text-zinc-500">
                      <Gift className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p>No hay códigos de descuento</p>
                    </td>
                  </tr>
                ) : discounts.map((discount) => (
                  <tr key={discount.code} className="border-b border-white/5 hover:bg-white/5">
                    <td className="p-4">
                      <span className="font-mono text-primary font-bold text-lg">
                        {discount.code}
                      </span>
                    </td>
                    <td className="p-4">
                      <p className="text-white">{discount.name || '-'}</p>
                      {discount.contact_email && (
                        <p className="text-xs text-zinc-500">{discount.contact_email}</p>
                      )}
                      {discount.campaign_origin && (
                        <Badge className="bg-zinc-700 text-zinc-300 text-xs mt-1">
                          {discount.campaign_origin}
                        </Badge>
                      )}
                    </td>
                    <td className="p-4">
                      <TypeBadge type={discount.type} value={discount.value} />
                      {discount.min_amount_cents > 0 && (
                        <p className="text-xs text-zinc-500 mt-1">
                          Mín: {(discount.min_amount_cents / 100).toFixed(2)}€
                        </p>
                      )}
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <span className="text-white font-medium">{discount.current_uses || 0}</span>
                        <span className="text-zinc-500">/</span>
                        {discount.max_uses === 0 ? (
                          <Infinity className="w-4 h-4 text-zinc-400" />
                        ) : (
                          <span className="text-zinc-400">{discount.max_uses || 1}</span>
                        )}
                      </div>
                    </td>
                    <td className="p-4 text-zinc-400 text-sm">
                      {format(new Date(discount.expires_at), 'dd/MM/yyyy', { locale: es })}
                    </td>
                    <td className="p-4">
                      <StatusBadge code={discount} />
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => toggleActive(discount.code, discount.is_active !== false)}
                          className={discount.is_active !== false ? 'text-green-400' : 'text-zinc-500'}
                        >
                          {discount.is_active !== false ? (
                            <ToggleRight className="w-5 h-5" />
                          ) : (
                            <ToggleLeft className="w-5 h-5" />
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEdit(discount)}
                          className="text-zinc-400 hover:text-white"
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(discount.code)}
                          className="text-red-400 hover:text-red-300"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {total > 20 && (
          <div className="p-4 border-t border-white/5 flex items-center justify-between">
            <p className="text-sm text-zinc-500">
              Mostrando {discounts.length} de {total} códigos
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page === 1}
                onClick={() => setPage(p => p - 1)}
                className="border-white/10"
              >
                Anterior
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={discounts.length < 20}
                onClick={() => setPage(p => p + 1)}
                className="border-white/10"
              >
                Siguiente
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="bg-background border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Gift className="w-5 h-5 text-primary" />
              Nuevo Código de Descuento
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-white">Nombre *</Label>
              <Input
                value={newDiscount.name}
                onChange={(e) => setNewDiscount({...newDiscount, name: e.target.value})}
                placeholder="Promoción Verano"
                className="bg-secondary border-white/10 mt-1"
              />
            </div>

            <div>
              <Label className="text-white">Código (opcional)</Label>
              <Input
                value={newDiscount.code}
                onChange={(e) => setNewDiscount({...newDiscount, code: e.target.value.toUpperCase()})}
                placeholder="Se genera automáticamente"
                className="bg-secondary border-white/10 mt-1 font-mono"
              />
              <p className="text-xs text-zinc-500 mt-1">Déjalo vacío para generar uno automático</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-white">Tipo</Label>
                <Select 
                  value={newDiscount.type} 
                  onValueChange={(v) => setNewDiscount({...newDiscount, type: v})}
                >
                  <SelectTrigger className="bg-secondary border-white/10 mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="PERCENTAGE">Porcentaje (%)</SelectItem>
                    <SelectItem value="FIXED">Cantidad fija (€)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-white">
                  Valor {newDiscount.type === 'PERCENTAGE' ? '(%)' : '(€)'}
                </Label>
                <Input
                  type="number"
                  value={newDiscount.value}
                  onChange={(e) => setNewDiscount({...newDiscount, value: parseFloat(e.target.value) || 0})}
                  className="bg-secondary border-white/10 mt-1"
                  min={0}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-white">Límite de usos</Label>
                <Input
                  type="number"
                  value={newDiscount.max_uses}
                  onChange={(e) => setNewDiscount({...newDiscount, max_uses: parseInt(e.target.value) || 0})}
                  className="bg-secondary border-white/10 mt-1"
                  min={0}
                />
                <p className="text-xs text-zinc-500 mt-1">0 = ilimitado</p>
              </div>
              <div>
                <Label className="text-white">Validez (días)</Label>
                <Input
                  type="number"
                  value={newDiscount.valid_days}
                  onChange={(e) => setNewDiscount({...newDiscount, valid_days: parseInt(e.target.value) || 30})}
                  className="bg-secondary border-white/10 mt-1"
                  min={1}
                />
              </div>
            </div>

            <div>
              <Label className="text-white">Mínimo de compra (€)</Label>
              <Input
                type="number"
                value={newDiscount.min_amount_cents}
                onChange={(e) => setNewDiscount({...newDiscount, min_amount_cents: parseFloat(e.target.value) || 0})}
                className="bg-secondary border-white/10 mt-1"
                min={0}
                step={0.01}
              />
              <p className="text-xs text-zinc-500 mt-1">0 = sin mínimo</p>
            </div>

            <div>
              <Label className="text-white">Email asociado (opcional)</Label>
              <Input
                type="email"
                value={newDiscount.contact_email}
                onChange={(e) => setNewDiscount({...newDiscount, contact_email: e.target.value})}
                placeholder="cliente@email.com"
                className="bg-secondary border-white/10 mt-1"
              />
            </div>
          </div>

          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setShowCreate(false)} className="border-white/10">
              Cancelar
            </Button>
            <Button onClick={handleCreate} disabled={creating} className="bg-primary text-black">
              {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Crear Código
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={showEdit} onOpenChange={setShowEdit}>
        <DialogContent className="bg-background border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Edit2 className="w-5 h-5 text-primary" />
              Editar Código: {editingCode}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-white">Nombre</Label>
              <Input
                value={editData.name || ''}
                onChange={(e) => setEditData({...editData, name: e.target.value})}
                className="bg-secondary border-white/10 mt-1"
              />
            </div>

            <div>
              <Label className="text-white">Valor</Label>
              <Input
                type="number"
                value={editData.value ?? ''}
                onChange={(e) => {
                  const val = e.target.value;
                  setEditData({...editData, value: val === '' ? '' : parseFloat(val)});
                }}
                className="bg-secondary border-white/10 mt-1"
                min={0}
                step={0.01}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-white">Límite de usos</Label>
                <Input
                  type="number"
                  value={editData.max_uses ?? ''}
                  onChange={(e) => {
                    const val = e.target.value;
                    setEditData({...editData, max_uses: val === '' ? '' : parseInt(val)});
                  }}
                  className="bg-secondary border-white/10 mt-1"
                  min={0}
                />
              </div>
              <div>
                <Label className="text-white">Mínimo (€)</Label>
                <Input
                  type="number"
                  value={editData.min_amount_cents ?? ''}
                  onChange={(e) => {
                    const val = e.target.value;
                    setEditData({...editData, min_amount_cents: val === '' ? 0 : parseFloat(val)});
                  }}
                  className="bg-secondary border-white/10 mt-1"
                  min={0}
                  step={0.01}
                />
              </div>
            </div>

            <div className="flex items-center justify-between p-4 bg-secondary rounded-lg">
              <div>
                <p className="text-white font-medium">Estado</p>
                <p className="text-sm text-zinc-500">
                  {editData.is_active ? 'El código está activo' : 'El código está desactivado'}
                </p>
              </div>
              <Button
                variant={editData.is_active ? "default" : "outline"}
                onClick={() => setEditData({...editData, is_active: !editData.is_active})}
                className={editData.is_active ? 'bg-green-600 hover:bg-green-700' : 'border-white/10'}
              >
                {editData.is_active ? 'Activo' : 'Inactivo'}
              </Button>
            </div>
          </div>

          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setShowEdit(false)} className="border-white/10">
              Cancelar
            </Button>
            <Button onClick={handleUpdate} disabled={saving} className="bg-primary text-black">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Guardar Cambios
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DiscountsManager;
